"use client";

import { useState } from "react";
import {
  CopyButton,
  Notice,
  StatCard,
  StatGrid,
  TextAreaField,
} from "@/components/tool-kit/primitives";
import {
  BUTTON_SECONDARY,
  WIDGET_H2,
  WIDGET_H3,
} from "@/components/tool-kit/styles";
import {
  Explanation,
  Formula,
  ResultTable,
  WorkingPanel,
} from "@/components/tools/stats-descriptive/ui";
import { HistogramPlot } from "@/components/tools/stats-descriptive-more/histogram-plot";
import {
  useToolLocale,
  useToolNumber,
  useToolUi,
} from "@/components/tools/tool-ui";
import { t, tPlural } from "@/lib/i18n/interpolate";
import { parseNumberList } from "@/lib/parse-number";
import {
  formatPValue,
  formatStat,
  type NumberFormatter,
} from "@/lib/tool-kit/stats-descriptive/format";
import {
  histogramBins,
  type MomentsReport,
  type MomentTest,
  momentsReport,
  skewLabel,
  tailLabel,
} from "@/lib/tool-kit/stats-descriptive-more/moments";

/** English strings. A translated content file overrides these through `ui`. */
export const SKEWNESS_AND_KURTOSIS_CALCULATOR_STRINGS = {
  dataLabel: "Your data — numbers separated by commas, spaces, or new lines",
  dataPlaceholder: "2.1, 2.4, 2.6, 2.7, 2.9, 3, 3.2, 3.5, 3.9, 4.6, 5.8, 8.2",
  valuesDetected: "one: value detected|other: values detected",
  tryExample: "Try a skewed example",
  trySymmetric: "Try a symmetric example",
  exampleSkewed: "2.1, 2.4, 2.6, 2.7, 2.9, 3, 3.2, 3.5, 3.9, 4.6, 5.8, 8.2",
  exampleSymmetric: "1, 2, 2, 3, 3, 3, 4, 4, 4, 4, 5, 5, 5, 6, 6, 7",
  clear: "Clear",
  enterPrompt:
    "Enter at least three numbers with some spread to see skewness; kurtosis needs four.",
  results: "Results",
  cardSkew: "Skewness (G1)",
  cardKurt: "Excess kurtosis (G2)",
  cardN: "Values (n)",
  cardMean: "Mean",
  cardMedian: "Median",
  cardSd: "Standard deviation (n − 1)",
  skewSymmetric:
    "G1 = {value}: the distribution is approximately symmetric (|skewness| below 0.5).",
  skewModerateRight:
    "G1 = {value}: moderately skewed to the right — a longer tail of high values (|skewness| between 0.5 and 1).",
  skewModerateLeft:
    "G1 = {value}: moderately skewed to the left — a longer tail of low values (|skewness| between 0.5 and 1).",
  skewHighRight:
    "G1 = {value}: highly skewed to the right — a long tail of high values (|skewness| above 1).",
  skewHighLeft:
    "G1 = {value}: highly skewed to the left — a long tail of low values (|skewness| above 1).",
  tailsNormal:
    "G2 = {value}: tails about as heavy as a normal distribution (excess kurtosis within ±0.5).",
  tailsHeavy:
    "G2 = {value}: heavier tails than a normal distribution (leptokurtic) — more extreme values than a bell curve predicts.",
  tailsLight:
    "G2 = {value}: lighter tails than a normal distribution (platykurtic) — fewer extreme values than a bell curve predicts.",
  needFourForKurtosis:
    "Kurtosis (G2) needs at least four values, so only skewness is reported.",
  testsTitle: "Tests against zero (normal population)",
  colStatistic: "Statistic",
  colValue: "Value",
  colSe: "Standard error",
  colZ: "z",
  colP: "p-value",
  colCi: "95% CI",
  rowSkew: "Skewness G1",
  rowKurt: "Excess kurtosis G2",
  ciTemplate: "{lower} to {upper}",
  testSignificant:
    "{name}: z = {z}, p {p} — different from zero at the 5% level.",
  testNotSignificant:
    "{name}: z = {z}, p {p} — not different from zero at the 5% level.",
  testNote:
    "Reference rule for small and medium samples (Kim, 2013): treat |z| above 1.96 as non-normal when n < 50, above 3.29 when 50 ≤ n < 300, and judge by the size of skewness and kurtosis themselves (about 2 and 7) when n ≥ 300.",
  typesTitle: "The three kinds of skewness and kurtosis",
  colType: "Formula",
  colSkew: "Skewness",
  colExcess: "Excess kurtosis",
  colPearson: "Kurtosis (Pearson, 3 for normal)",
  colUsedBy: "Used by",
  typeG: "g1, g2 — moment ratios (e1071 type 1)",
  typeAdjusted: "G1, G2 — sample-adjusted (e1071 type 2)",
  typeB: "b1, b2 — based on s (e1071 type 3)",
  usedG: "Textbook definitions; the population values",
  usedAdjusted: "SPSS, SAS, Excel SKEW and KURT, SciPy bias=False",
  usedB: "Minitab",
  notAvailable: "—",
  plotTitle: "Histogram with a normal curve",
  plotLabel:
    "Histogram of {n} values with the normal curve of the same mean and standard deviation overlaid.",
  plotYLabel: "Frequency",
  plotNote:
    "The orange-red line is the normal curve with the same mean and standard deviation, scaled to the bars.",
  pearsonTitle: "Pearson's second coefficient of skewness",
  pearsonLine:
    "3 × (mean − median) ÷ s = 3 × ({mean} − {median}) ÷ {sd} = {value}",
  workingTitle: "How the skewness and kurtosis were calculated",
  workMoments:
    "Central moments: m₂ = Σ(x − x̄)² ÷ n = {m2}, m₃ = Σ(x − x̄)³ ÷ n = {m3}, m₄ = Σ(x − x̄)⁴ ÷ n = {m4}",
  workG1: "g1 = m₃ ÷ m₂^1.5 = {m3} ÷ {m2}^1.5 = {g1}",
  workAdjustedSkew:
    "G1 = g1 × √(n(n − 1)) ÷ (n − 2) = {g1} × √({n} × {nm1}) ÷ {nm2} = {value}",
  workB1: "b1 = g1 × ((n − 1) ÷ n)^1.5 = {value}",
  workG2: "g2 = m₄ ÷ m₂² − 3 = {m4} ÷ {m2}² − 3 = {g2}",
  workAdjustedKurt:
    "G2 = ((n − 1) ÷ ((n − 2)(n − 3))) × ((n + 1) × g2 + 6) = ({nm1} ÷ ({nm2} × {nm3})) × (({np1} × {g2}) + 6) = {value}",
  workB2: "b2 = (g2 + 3) × (1 − 1 ÷ n)² − 3 = {value}",
  workSeSkew: "SE(G1) = √(6n(n − 1) ÷ ((n − 2)(n + 1)(n + 3))) = {value}",
  workSeKurt: "SE(G2) = 2 × SE(G1) × √((n² − 1) ÷ ((n − 3)(n + 5))) = {value}",
  copySummary: "Copy summary",
  copied: "Copied!",
  summaryText:
    "Skewness = {skew} (SE = {seSkew}), excess kurtosis = {kurt} (SE = {seKurt}), n = {n}",
  summarySkewOnly: "Skewness = {skew} (SE = {seSkew}), n = {n}",
} as const;

type Strings = typeof SKEWNESS_AND_KURTOSIS_CALCULATOR_STRINGS;

interface View {
  strings: Strings;
  fmt: NumberFormatter;
  num: (value: number) => string;
}

function skewSentence(skew: number, view: View): string {
  const { strings, num } = view;
  const value = num(skew);
  const label = skewLabel(skew);
  if (label === "symmetric") {
    return t(strings.skewSymmetric, { value });
  }
  if (label === "moderate") {
    return t(skew > 0 ? strings.skewModerateRight : strings.skewModerateLeft, {
      value,
    });
  }
  return t(skew > 0 ? strings.skewHighRight : strings.skewHighLeft, { value });
}

function tailSentence(excess: number, view: View): string {
  const { strings, num } = view;
  const label = tailLabel(excess);
  const value = num(excess);
  if (label === "heavy") {
    return t(strings.tailsHeavy, { value });
  }
  return t(label === "light" ? strings.tailsLight : strings.tailsNormal, {
    value,
  });
}

function testRow(name: string, test: MomentTest, view: View): string[] {
  const { strings, fmt, num } = view;
  return [
    name,
    num(test.value),
    num(test.se),
    num(test.z),
    formatPValue(fmt, test.p),
    t(strings.ciTemplate, { lower: num(test.lower), upper: num(test.upper) }),
  ];
}

function testSentence(name: string, test: MomentTest, view: View): string {
  const { strings, fmt, num } = view;
  const p = formatPValue(fmt, test.p);
  return t(
    test.p < 0.05 ? strings.testSignificant : strings.testNotSignificant,
    { name, z: num(test.z), p: p.startsWith("<") ? p : `= ${p}` }
  );
}

function typeRows(report: MomentsReport, view: View): string[][] {
  const { strings, num } = view;
  const { skewness, kurtosis } = report;
  const optional = (value: number | null) =>
    value === null ? strings.notAvailable : num(value);
  const pearson = (value: number | null) =>
    value === null ? strings.notAvailable : num(value + 3);
  return [
    [
      strings.typeG,
      num(skewness.g1),
      num(kurtosis.g2),
      pearson(kurtosis.g2),
      strings.usedG,
    ],
    [
      strings.typeAdjusted,
      optional(skewness.adjusted),
      optional(kurtosis.adjusted),
      pearson(kurtosis.adjusted),
      strings.usedAdjusted,
    ],
    [
      strings.typeB,
      num(skewness.b1),
      num(kurtosis.b2),
      pearson(kurtosis.b2),
      strings.usedB,
    ],
  ];
}

function Working({ report, view }: { report: MomentsReport; view: View }) {
  const { strings, fmt, num } = view;
  const { n, m2, m3, m4, skewness, kurtosis } = report;
  const shared = {
    n: fmt(n),
    nm1: fmt(n - 1),
    nm2: fmt(n - 2),
    nm3: fmt(n - 3),
    np1: fmt(n + 1),
    m2: num(m2),
    m3: num(m3),
    m4: num(m4),
    g1: num(skewness.g1),
    g2: num(kurtosis.g2),
  };
  return (
    <WorkingPanel title={strings.workingTitle}>
      <Formula>{t(strings.workMoments, shared)}</Formula>
      <Formula>{t(strings.workG1, shared)}</Formula>
      <Formula>
        {t(strings.workAdjustedSkew, {
          ...shared,
          value: num(skewness.adjusted ?? Number.NaN),
        })}
      </Formula>
      <Formula>{t(strings.workB1, { value: num(skewness.b1) })}</Formula>
      {kurtosis.adjusted === null ? null : (
        <>
          <Formula>{t(strings.workG2, shared)}</Formula>
          <Formula>
            {t(strings.workAdjustedKurt, {
              ...shared,
              value: num(kurtosis.adjusted),
            })}
          </Formula>
          <Formula>{t(strings.workB2, { value: num(kurtosis.b2) })}</Formula>
        </>
      )}
      {report.skewTest ? (
        <Formula>
          {t(strings.workSeSkew, { value: num(report.skewTest.se) })}
        </Formula>
      ) : null}
      {report.kurtosisTest ? (
        <Formula>
          {t(strings.workSeKurt, { value: num(report.kurtosisTest.se) })}
        </Formula>
      ) : null}
      <Formula>
        {t(strings.pearsonLine, {
          mean: num(report.mean),
          median: num(report.median),
          sd: num(report.sd),
          value: num(report.pearsonSecond),
        })}
      </Formula>
    </WorkingPanel>
  );
}

export default function SkewnessAndKurtosisCalculatorWidget() {
  const strings = useToolUi(SKEWNESS_AND_KURTOSIS_CALCULATOR_STRINGS);
  const locale = useToolLocale();
  const fmt = useToolNumber();
  const [raw, setRaw] = useState("");

  const values = parseNumberList(raw);
  const report = momentsReport(values);
  const num = (value: number) => formatStat(fmt, value);
  const view: View = { strings, fmt, num };
  const skew = report?.skewTest ?? null;
  const kurt = report?.kurtosisTest ?? null;

  return (
    <>
      <section className="space-y-4">
        <TextAreaField
          hint={
            <>
              {fmt(values.length)}
              {tPlural(strings.valuesDetected, locale, values.length)}
            </>
          }
          id="sk-data"
          label={strings.dataLabel}
          onChange={setRaw}
          placeholder={strings.dataPlaceholder}
          value={raw}
        />
        <div className="flex flex-wrap gap-3">
          <button
            className={BUTTON_SECONDARY}
            onClick={() => setRaw(strings.exampleSkewed)}
            type="button"
          >
            {strings.tryExample}
          </button>
          <button
            className={BUTTON_SECONDARY}
            onClick={() => setRaw(strings.exampleSymmetric)}
            type="button"
          >
            {strings.trySymmetric}
          </button>
          <button
            className={BUTTON_SECONDARY}
            disabled={raw === ""}
            onClick={() => setRaw("")}
            type="button"
          >
            {strings.clear}
          </button>
        </div>
      </section>

      {report && skew ? (
        <section className="space-y-6">
          <h2 className={WIDGET_H2}>{strings.results}</h2>
          <div aria-live="polite" className="space-y-4">
            <StatGrid>
              <StatCard
                emphasis
                label={strings.cardSkew}
                value={num(skew.value)}
              />
              <StatCard
                emphasis
                label={strings.cardKurt}
                value={kurt ? num(kurt.value) : strings.notAvailable}
              />
              <StatCard label={strings.cardN} value={fmt(report.n)} />
              <StatCard label={strings.cardMean} value={num(report.mean)} />
              <StatCard label={strings.cardMedian} value={num(report.median)} />
              <StatCard label={strings.cardSd} value={num(report.sd)} />
            </StatGrid>
            <Explanation>{skewSentence(skew.value, view)}</Explanation>
            {kurt ? (
              <Explanation>{tailSentence(kurt.value, view)}</Explanation>
            ) : (
              <Explanation>{strings.needFourForKurtosis}</Explanation>
            )}
            <CopyButton
              copiedLabel={strings.copied}
              label={strings.copySummary}
              text={t(kurt ? strings.summaryText : strings.summarySkewOnly, {
                skew: num(skew.value),
                seSkew: num(skew.se),
                kurt: kurt ? num(kurt.value) : "",
                seKurt: kurt ? num(kurt.se) : "",
                n: fmt(report.n),
              })}
            />
          </div>

          <div className="space-y-2">
            <h3 className={WIDGET_H3}>{strings.testsTitle}</h3>
            <ResultTable
              head={[
                strings.colStatistic,
                strings.colValue,
                strings.colSe,
                strings.colZ,
                strings.colP,
                strings.colCi,
              ]}
              rows={[
                testRow(strings.rowSkew, skew, view),
                ...(kurt ? [testRow(strings.rowKurt, kurt, view)] : []),
              ]}
              wrapFirstColumn
            />
            <Explanation>
              {testSentence(strings.rowSkew, skew, view)}
            </Explanation>
            {kurt ? (
              <Explanation>
                {testSentence(strings.rowKurt, kurt, view)}
              </Explanation>
            ) : null}
            <Explanation>{strings.testNote}</Explanation>
          </div>

          <div className="space-y-2">
            <h3 className={WIDGET_H3}>{strings.typesTitle}</h3>
            <ResultTable
              head={[
                strings.colType,
                strings.colSkew,
                strings.colExcess,
                strings.colPearson,
                strings.colUsedBy,
              ]}
              rows={typeRows(report, view)}
              wrapFirstColumn
            />
          </div>

          <div className="space-y-2">
            <h3 className={WIDGET_H3}>{strings.plotTitle}</h3>
            <HistogramPlot
              bins={histogramBins(values)}
              fmt={fmt}
              label={t(strings.plotLabel, { n: fmt(report.n) })}
              normal={{
                mean: report.mean,
                sd: Math.sqrt(report.m2),
                n: report.n,
              }}
              yLabel={strings.plotYLabel}
            />
            <p className="text-muted-foreground text-xs">{strings.plotNote}</p>
          </div>

          <Working report={report} view={view} />
        </section>
      ) : (
        <Notice>{strings.enterPrompt}</Notice>
      )}
    </>
  );
}
